<template>
  <div class="login-shell d-flex justify-content-center align-items-center">
      <div v-if="isLoading" class="login-loader">
        <div class="loader"></div>
      </div>
      
      <div v-else class="card p-4 login-card">
        <h2 class="text-center mb-4">{{ $t('auth.login.title') }}</h2>
        
        <form @submit.prevent="login" novalidate>
          <div class="mb-3">
              <label for="userEmail" class="form-label">{{ $t('auth.login.email') }}</label>
              <input 
                id="userEmail"
                type="email"
                class="form-control"
                v-model="formData.userEmail"
                @blur="emailTouched = true"
                autocomplete="username"
                required
              />
          </div>

          <div class="mb-3">
              <label for="userPassword" class="form-label">{{ $t('auth.login.password') }}</label>
              <div class="input-group">
                  <input
                    id="userPassword"
                    :type="passwordFieldType"
                    class="form-control"
                    v-model="formData.userPassword"
                    @blur="passwordTouched = true"
                    autocomplete="current-password"
                    required
                  />
                  <button 
                    type="button"
                    class="btn btn-outline-secondary btn-password-toggle"
                    @click="togglePasswordVisibility"
                    :aria-label="passwordFieldType === 'password' ? $t('auth.login.showPassword') : $t('auth.login.hidePassword')"
                  >
                    <i :class="passwordIcon"></i>
                  </button>
              </div>
          </div>

          <!-- Forgot Password link -->
          <div class="mb-3 text-end">
            <router-link to="/forgot-password">
              {{ $t('auth.login.forgotPassword') }}
            </router-link>
          </div>

          <div v-if="loginError" class="error-message">{{ loginError }}</div>

          <button type="submit" class="stora-btn-primary w-100">
            <span>{{ $t('auth.login.submit') }}</span>
          </button>
        </form>
      </div>
    </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import image from "./icons/logo_storalytic.png"

import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useFlowStore } from '@/stores/FlowStore'

export default {
    name: 'Login',

    setup() {
        const router = useRouter()
        const { t } = useI18n()

        const userStore = useUserStore()
        const companyStore = useCompanyStore()
        const flowStore = useFlowStore()
        

        const passwordVisible = ref(false)
        const emailTouched = ref(false)
        const passwordTouched = ref(false)
        const loginError = ref(null)

        const formData = ref({
            userEmail: '',
            userPassword: ''
        })

        const isLoading = ref(false)

        // Reactive properties for password visibility
        const passwordFieldType = computed(() => passwordVisible.value ? 'text' : 'password')
        const passwordIcon = computed(() => passwordVisible.value ? 'bi bi-eye-fill' : 'bi bi-eye-slash-fill')

        // Method to toggle password visibility
        const togglePasswordVisibility = () => {
            passwordVisible.value = !passwordVisible.value
        }

        const login = async () => {
          loginError.value = null
          isLoading.value = true

          try {
                await userStore.loginUser(formData.value)
                await companyStore.fetchCompanyDetails(userStore.currentCompany.CompanyId)
                await flowStore.fetchFlows()
                

                formData.value = {
                    userEmail: '',
                    userPassword: ''
                }

                if (userStore.companies.length > 1 || userStore.isSuperAdmin) {
                  router.push('/select-company')
                }
                else {
                  router.push('/')
                }
            } catch (error) {
                isLoading.value = false

                if (error.response && error.response.status === 401) {
                    loginError.value = t('auth.login.invalidCredentials')
                } else {
                    loginError.value = t('auth.login.genericError')
                }
                console.error("Login failed: ", error.message);
            }
        }

        onBeforeUnmount(() => {
                isLoading.value = false
        })

        return {
            logo_applicaite: image,
            formData,
            login,
            loginError,
            passwordFieldType,
            togglePasswordVisibility,
            passwordIcon,
            emailTouched,
            passwordTouched,
            isLoading,
        }
    }
}
</script>

<style scoped>
/* Layout */
.login-shell { min-height: 100vh; padding: 24px; }
.login-loader { display:flex; justify-content:center; align-items:flex-start; width:100%; }
.login-card { width: 100%; max-width: 480px; }

/* Primary action (aligns with Storalytic colors) */
.stora-btn-primary{
  display:inline-flex; align-items:center; justify-content:center;
  gap:8px; padding:10px 16px; border-radius:8px;
  background:#4F7EB3; color:#fff; border:0; cursor:pointer;
  transition: background .2s ease, transform .02s ease;
}
.stora-btn-primary:hover{ background:#B2C149; }
.stora-btn-primary:disabled{ opacity:.7; cursor:not-allowed; }

.btn-password-toggle {
    background-color: white;
    border: 1px solid;
    border-color: #CED4DA;
}

.form-control {
  max-width: 500px;
}

.error-message{
    margin-bottom: 15px;
    color: #C21500;
}

.loader {
  width: 80px;
  padding: 8px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #B2C149;
  --_m: 
    conic-gradient(#0000 10%,#000),
    linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m);
          mask: var(--_m);
  -webkit-mask-composite: source-out;
          mask-composite: subtract;
  animation: spinner 1s infinite linear;
}
@keyframes spinner {to{transform: rotate(1turn)}}
</style>